import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { DataLoadedField } from '@adornis/baseql/decorators/data-loaded-field.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import type { PartialEntityData } from '@adornis/baseql/entities/types.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { mongo } from '@adornis/buchungsheld-db/server/mongo.js';
import { loadMongoData } from '@adornis/baseql/server/data-loader.js';
import { User } from '@adornis/buchungsheld-db/entities/user.js';
import { JobPosition } from './job-position.js';
import { MatchingScore } from './MatchingScore.js';
import { MatchingWeekSettings } from './settings.js';
import { MWUserData } from './user.js';

export enum JobApplicationStatus {
  PENDING = 'PENDING',
  ACCEPTED = 'ACCEPTED',
  REJECTED = 'REJECTED',
  REJECTED_BY_USER = 'REJECTED_BY_USER',
  WAITING = 'WAITING',
  BOOKED = 'BOOKED',
}

registerEnum({ enum: JobApplicationStatus, name: 'JobApplicationStatus' });

@Entity()
export class JobApplication extends MongoEntity {
  static override _class = 'JobApplication';
  static override _collectionName = 'jobApplications';

  @Field(() => ID)
  jobPositionId: Maybe<string>;

  @Field(() => ID)
  userId: Maybe<string>;

  @DataLoadedField(() => User, app => app.userId)
  user: Maybe<User>;

  @DataLoadedField(() => JobPosition, app => app.jobPositionId)
  jobPosition: Maybe<JobPosition>;

  @Field(() => MWUserData, {
    resolve: function (this: JobApplication, gqlFields, context, info) {
      return async () => {
        if (!this.userId) throw new Error('No user ID');
        const userData = (await loadMongoData(MWUserData, { userID: this.userId }))[0];
        return userData ? new MWUserData(userData) : undefined;
      };
    },
  })
  userData: Maybe<MWUserData>;

  @Field(() => JobApplicationStatus)
  status: Maybe<JobApplicationStatus>;

  @Field(() => Date)
  createdAt: Maybe<Date>;

  @Field(() => Date)
  updatedAt: Maybe<Date>;

  @Field(() => Date)
  interviewStart: Maybe<Date>;

  @Field(() => Date)
  interviewEnd: Maybe<Date>;

  // Whether the interview actually took place, confirmed by the company after the appointment.
  // null/undefined = not yet confirmed, true = attended, false = did not take place.
  @Field(() => Boolean)
  interviewAttended: Maybe<boolean>;

  @Field(() => MatchingScore, {
    resolve: function (this: JobApplication) {
      if (!this.userId) throw new Error('No user ID');
      if (!this.jobPositionId) throw new Error('No job position ID');
      const userID = this.userId;
      const jobPositionID = this.jobPositionId;
      return async (
        gqlFields: import('@adornis/baseql/utils/queryGeneration.js').BaseQLSelectionSet<MatchingScore>,
      ) => {
        const { getOrCalculateMatchingScore } = await import('../api/matching-score.js');
        return getOrCalculateMatchingScore(userID, jobPositionID)(gqlFields);
      };
    },
  })
  matchingScore: Maybe<MatchingScore>;

  static override async beforeCreate(entity: JobApplication): Promise<void> {
    const settingsCollection = mongo().collection(MatchingWeekSettings._collectionName);
    const settings = await settingsCollection.findOne({}, { projection: { applicationPhaseClosed: 1 } });
    if (settings?.applicationPhaseClosed !== false) throw new Error('Application phase is closed');

    entity.createdAt = new Date();
    entity.updatedAt = new Date();
    await super.beforeCreate(entity);
  }

  static override transformBeforeUpdate(
    oldEntity: PartialEntityData<JobApplication>,
    update: JobApplication,
  ): Promise<void> {
    update.updatedAt = new Date();
    return super.transformBeforeUpdate(oldEntity, update);
  }
}
